import { useEffect, useState } from 'react';
import { Box, Heading, Stack, Text } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { createClient, listSellers } from '@/lib/api';
import { useProject } from '@/lib/ProjectContext';
import { toaster } from '@/components/ui/toaster';
import { EMPTY_CLIENT_FORM, ClientForm, type ClientFormValues } from './ClientForm';

export function ClientNew() {
  const navigate = useNavigate();
  const { activeProject } = useProject();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sellers, setSellers] = useState<{ id: string; fullName: string }[]>([]);

  useEffect(() => {
    listSellers({ status: 'active' })
      .then((rows) => setSellers(rows.map((s) => ({ id: s.id, fullName: s.fullName }))))
      .catch(() => setSellers([]));
  }, []);

  async function handleSubmit(values: ClientFormValues) {
    if (!activeProject) {
      setError('Selecciona un proyecto activo antes de crear un cliente.');
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const result = await createClient({
        projectId: activeProject.id,
        fullName: values.fullName.trim(),
        documentType: values.documentType,
        documentNumber: values.documentNumber.trim(),
        email: values.email.trim() || null,
        phone: values.phone.trim() || null,
        address: values.address.trim() || null,
        notes: values.notes.trim() || null,
        accountOwnerSellerId: values.accountOwnerSellerId || null,
      });
      if ('conflict' in result) {
        const c = result.conflict;
        const label = c.conflict === 'document' ? 'documento' : 'correo';
        const existing = c.existingClientName ? ` (${c.existingClientName})` : '';
        setError(
          `Ya existe un cliente con este ${label}${existing}. ` +
            'Búscalo en la lista y agrégalo a este proyecto desde su ficha.',
        );
        return;
      }
      toaster.create({
        title: 'Cliente creado',
        description: `${result.code} · ${values.fullName}`,
        type: 'success',
      });
      navigate('/clientes');
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'No se pudo crear';
      setError(msg);
      toaster.create({ title: 'No se pudo crear el cliente', description: msg, type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  if (!activeProject) {
    return (
      <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
        <Box
          bg="bg.surface"
          borderWidth="1px"
          borderColor="border"
          rounded="lg"
          p="10"
          textAlign="center"
          maxW="2xl"
          mx="auto"
        >
          <Text color="fg.muted">
            Selecciona un proyecto activo en la barra superior antes de crear un cliente.
          </Text>
        </Box>
      </Box>
    );
  }

  return (
    <Box px={{ base: '4', md: '8' }} py={{ base: '6', md: '8' }}>
      <Stack gap="6" maxW="3xl">
        <Stack gap="1">
          <Text
            fontSize="xs"
            color="fg.muted"
            textTransform="uppercase"
            letterSpacing="0.06em"
            fontWeight="700"
          >
            {activeProject.code} · Clientes
          </Text>
          <Heading size={{ base: 'xl', md: '2xl' }} letterSpacing="-0.02em">
            Nuevo cliente
          </Heading>
          <Text color="fg.muted" fontSize={{ base: 'sm', md: 'md' }}>
            El cliente recibirá automáticamente un código <strong>{activeProject.code}-NNN</strong>{' '}
            al guardar.
          </Text>
        </Stack>
        <ClientForm
          initial={EMPTY_CLIENT_FORM}
          submitLabel="Crear cliente"
          submitting={submitting}
          error={error}
          sellers={sellers}
          onSubmit={handleSubmit}
          onCancel={() => navigate('/clientes')}
        />
      </Stack>
    </Box>
  );
}
